// Lib
import { useEffect, useLayoutEffect, useRef } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import Cropper from 'cropperjs';

// Utils
import { loadCanvasIntoCropper, replaceCropperImage } from '../utils/cropperUtils';
import { decodeImageUrl } from '../utils/imageModalImageDecodeUtils';
import { acquireDecodedImage, type DecodedImageHandle } from '../utils/imageModalDecodedImageCache';
import { fetchImageObjectUrl } from '../../../imageFetchUtils';

// Types
import type { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

// Logging
import globalLogger from '../../../../../logger';

const logger = globalLogger.createChannel('image.modal.editor');

export const IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY = {
    CLEAR_IMAGE_BEFORE_LOAD: 'clearImageBeforeLoad',
    KEEP_PREVIOUS_IMAGE_UNTIL_READY: 'keepPreviousImageUntilReady',
} as const;

type ImageModalEditorImageLoadStrategy =
    (typeof IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY)[keyof typeof IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY];

const destroyCropper = (cropperRef: MutableRefObject<Cropper | null>) => {
    if (!cropperRef.current) return;

    cropperRef.current.destroy();
    cropperRef.current = null;
};

const loadDecodedEditorImage = async (sourceUrl: string, imageType: string): Promise<string> => {
    const { objectUrl } = await fetchImageObjectUrl(sourceUrl, imageType);

    try {
        await decodeImageUrl(objectUrl);
        return objectUrl;
    } catch (error) {
        URL.revokeObjectURL(objectUrl);
        throw error;
    }
};

/** Swaps in a new held handle for `holderRef`, releasing the previous one only after the new one is
 * acquired — so re-acquiring the same key mid-render never drops its ref count to zero in between. */
const swapHeldHandle = (
    holderRef: MutableRefObject<DecodedImageHandle | null>,
    nextHandle: DecodedImageHandle | null,
) => {
    const previousHandle = holderRef.current;
    holderRef.current = nextHandle;
    previousHandle?.release();
};

/**
 * Loads the cropper source image using the requested v1/v2 image replacement strategy.
 */
export const useImageModalEditorImageLoader = ({
    imageRef,
    cropperRef,
    isDirtyRef,
    element,
    imageType,
    loadKey,
    loadStrategy,
    sourceUrl,
    prefetchSource,
    onEditorReadyChange,
    onImageDataUrlLoad,
    applyStoredCropData = true,
    pendingCropperDataRef,
    onCropChange,
    recenterAfterTransforms = true,
    isPaused = false,
}: {
    imageRef: RefObject<HTMLImageElement | null>;
    cropperRef: MutableRefObject<Cropper | null>;
    isDirtyRef: MutableRefObject<boolean>;
    element: ImMNElement;
    imageType: string;
    loadKey: string;
    loadStrategy: ImageModalEditorImageLoadStrategy;
    sourceUrl?: string;
    prefetchSource?: { sourceUrl: string; imageType: string } | null;
    onEditorReadyChange: (ready: boolean) => void;
    onImageDataUrlLoad?: (dataUrl: string) => void;
    applyStoredCropData?: boolean;
    // Live rotate/flip/crop to re-apply after a background-removal source swap (see loadCanvasIntoCropper).
    pendingCropperDataRef?: MutableRefObject<Cropper.Data | null>;
    onCropChange?: () => void;
    recenterAfterTransforms?: boolean;
    /**
     * Holds the Cropper exactly as it is, ignoring every reload trigger. Used by the V2 save freeze: the
     * frozen editor must keep painting the frame the user pressed Save on, and a rebuild here would drop
     * the session's live rotate/flip (which lives on the Cropper, not in the frozen element snapshot) and
     * repaint the un-transformed source for the rest of the save.
     */
    isPaused?: boolean;
}) => {
    const elementRef = useRef(element);
    const loadGenerationRef = useRef(0);
    // The handle whose object URL the Cropper (or its replace cover) is currently painting. It must stay
    // acquired until the replacement source has committed — releasing it when the next load starts would
    // revoke the URL mid-swap and blank the still-visible outgoing frame.
    const displayedHandleRef = useRef<DecodedImageHandle | null>(null);
    const loadingHandleRef = useRef<DecodedImageHandle | null>(null);
    const heldPrefetchHandleRef = useRef<DecodedImageHandle | null>(null);
    const shouldClearImageBeforeLoad = loadStrategy === IMAGE_MODAL_EDITOR_IMAGE_LOAD_STRATEGY.CLEAR_IMAGE_BEFORE_LOAD;

    // Cropper's ready callback can run after React has received a newer element object.
    // Keep the ref current without restarting the image load for object-identity-only changes.
    elementRef.current = element;

    useLayoutEffect(
        () => () => {
            destroyCropper(cropperRef);
            swapHeldHandle(displayedHandleRef, null);
            swapHeldHandle(loadingHandleRef, null);
            swapHeldHandle(heldPrefetchHandleRef, null);
        },
        [cropperRef],
    );

    useLayoutEffect(() => {
        if (isPaused) return;

        const canKeepPreviousEditorVisible = !shouldClearImageBeforeLoad && !!cropperRef.current;
        if (!canKeepPreviousEditorVisible) onEditorReadyChange(false);

        if (shouldClearImageBeforeLoad) {
            destroyCropper(cropperRef);
            imageRef.current?.removeAttribute('src');
        }

        if (!imageRef.current || !sourceUrl) return;

        let cancelled = false;
        const loadGeneration = loadGenerationRef.current + 1;
        loadGenerationRef.current = loadGeneration;

        // Fetching the image and initialising Cropper both finish asynchronously. If a newer
        // image load starts first, the older callbacks must not replace the image or mark the
        // editor ready.
        const canUseLoadedImage = () => !cancelled && loadGenerationRef.current === loadGeneration;

        const handle = acquireDecodedImage(sourceUrl, imageType, () => loadDecodedEditorImage(sourceUrl, imageType));
        // A superseded in-flight load can release immediately; the displayed handle waits for commit.
        swapHeldHandle(loadingHandleRef, handle);

        // Promote the handle to "displayed" (and release the outgoing image) only once Cropper reports
        // the swapped-in source ready — the outgoing object URL is painted right up to that moment.
        const commitEditorReadyChange = (ready: boolean) => {
            onEditorReadyChange(ready);
            if (!ready || displayedHandleRef.current === handle) return;

            if (loadingHandleRef.current === handle) loadingHandleRef.current = null;
            swapHeldHandle(displayedHandleRef, handle);
        };

        const fetchAndCommitImage = async () => {
            try {
                const imageUrl = await handle.promise;

                // A later load may have started while this image was being fetched.
                if (!canUseLoadedImage()) return;

                if (imageRef.current instanceof HTMLImageElement) {
                    onImageDataUrlLoad?.(imageUrl);

                    if (cropperRef.current) {
                        replaceCropperImage({
                            cropperRef,
                            imageUrl,
                            element: elementRef.current,
                            setEditorIsReady: commitEditorReadyChange,
                            isDirtyRef,
                            canUseLoadedImage,
                            applyStoredCropData,
                            pendingCropperDataRef,
                            onCropChange,
                            recenterAfterTransforms,
                        });
                    } else {
                        imageRef.current.src = imageUrl;
                        loadCanvasIntoCropper({
                            imageRef,
                            cropperRef,
                            element: elementRef.current,
                            setEditorIsReady: commitEditorReadyChange,
                            isDirtyRef,
                            canUseLoadedImage,
                            applyStoredCropData,
                            pendingCropperDataRef,
                            onCropChange,
                            recenterAfterTransforms,
                        });
                    }
                }
            } catch (error) {
                // The decode never committed to displayed, so release this load's still-held handle
                // (unless a newer load already replaced it) rather than leaking it until unmount.
                if (loadingHandleRef.current === handle) swapHeldHandle(loadingHandleRef, null);
                // Sensitive data audit: SAFE — warn/error auto-sanitized (image file names/URLs redacted, error normalized)
                logger.error('Failed to load image for editing', error);
            }
        };

        fetchAndCommitImage();

        return () => {
            cancelled = true;
            if (shouldClearImageBeforeLoad) destroyCropper(cropperRef);
        };
    }, [
        applyStoredCropData,
        cropperRef,
        imageRef,
        imageType,
        isDirtyRef,
        isPaused,
        loadKey,
        onCropChange,
        onImageDataUrlLoad,
        onEditorReadyChange,
        pendingCropperDataRef,
        recenterAfterTransforms,
        shouldClearImageBeforeLoad,
        sourceUrl,
    ]);

    useEffect(() => {
        if (!prefetchSource) {
            swapHeldHandle(heldPrefetchHandleRef, null);
            return;
        }

        const handle = acquireDecodedImage(prefetchSource.sourceUrl, prefetchSource.imageType, () =>
            loadDecodedEditorImage(prefetchSource.sourceUrl, prefetchSource.imageType),
        );
        handle.promise.catch(() => undefined);
        swapHeldHandle(heldPrefetchHandleRef, handle);
    }, [prefetchSource]);
};
